// Story 10.1 — WebSocket client para el frontend. Reusa el contrato del CLI
// (Story 6.3) adaptado a browser:
//  - usa `WebSocket` global (no `ws` de Node).
//  - auth via cookie de sesión (mismo origin) — el browser la envía
//    automáticamente al hacer upgrade.
//  - lazy connect: solo abre cuando hay un suscriptor; cierra cuando el
//    último se desuscribe (tras un grace period para evitar reconnect storms).
//  - reconexión exponencial 1s → 30s con max 5 intentos.

export interface WsEnvelope<T = unknown> {
  type: string;
  event?: string;
  botId?: string;
  timestamp?: string;
  payload?: T;
}

export type WsEventHandler = (envelope: WsEnvelope) => void;

export interface WsClientOptions {
  /** URL completa del endpoint (`ws(s)://host/api/v1/ws`). Si no se pasa, se
   *  deriva del location actual. */
  url?: string;
  /** Inyectable para tests — default `globalThis.WebSocket`. */
  WebSocketImpl?: typeof WebSocket;
  /** Reloj inyectable para tests determinísticos. Default `Date.now`. */
  now?: () => number;
  /** Programador de timeouts inyectable. */
  scheduler?: {
    setTimeout: (fn: () => void, ms: number) => unknown;
    clearTimeout: (handle: unknown) => void;
  };
  /** Override del backoff. Default `[1000, 2000, 5000, 10000, 30000]`. */
  reconnectDelays?: readonly number[];
  /** ms tras el último unsubscribe antes de cerrar el socket. Default 1000. */
  idleCloseDelay?: number;
}

export interface WsClient {
  /** Suscribir un handler a un evento (`'*'` para todos). Retorna unsubscribe. */
  subscribe(eventName: string, handler: WsEventHandler): () => void;
  /** Estado actual del socket. */
  readyState(): number;
  /** Cierra el socket forzosamente. Útil en logout o tests. */
  close(): void;
  /**
   * Story 16.6 — cierra el socket y abre otro con las cookies de ahora. Tras
   * rotar la sesión (cambio de contraseña), el socket viejo sigue autenticado
   * con el token anterior hasta que el heartbeat del server lo cierra con 1008.
   */
  reconnect(): void;
}

const DEFAULT_DELAYS = [1000, 2000, 5000, 10_000, 30_000] as const;

function deriveUrl(): string {
  // P-214: en prod el web vive en `app.example.com` y la API en
  // `api.example.com` — `window.location.host` apunta al dominio
  // equivocado. Preferimos `VITE_WS_URL` explícito; si no, derivamos del
  // `VITE_API_URL` (http→ws / https→wss). Solo caemos al host actual en
  // dev (Vite proxy reenvía `/api/v1/ws`) o cuando ninguna env var se
  // setea.
  //
  // ⚠️ Cross-origin: para que el browser envíe la cookie `revel_session`
  // al hacer upgrade a `api.example.com` desde `app.example.com`, la
  // cookie debe emitirse con `Domain=.example.com` (Story 5.2 setting
  // `COOKIE_DOMAIN`). Si no, el WS se rechaza con 401 antes del upgrade.
  const wsEnv = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  const explicit = wsEnv?.VITE_WS_URL;
  if (explicit) return explicit;
  const apiUrl = wsEnv?.VITE_API_URL;
  if (apiUrl) {
    try {
      const u = new URL(apiUrl);
      const proto = u.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${proto}//${u.host}/api/v1/ws`;
    } catch {
      // cae al fallback de abajo
    }
  }
  if (typeof window === 'undefined') return 'ws://localhost:3000/api/v1/ws';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/api/v1/ws`;
}

export function createWsClient(options: WsClientOptions = {}): WsClient {
  const url = options.url ?? deriveUrl();
  const WS = options.WebSocketImpl ?? (globalThis.WebSocket as typeof WebSocket);
  const reconnectDelays = options.reconnectDelays ?? DEFAULT_DELAYS;
  const idleCloseDelay = Math.max(0, options.idleCloseDelay ?? 1000);
  const scheduler = options.scheduler ?? {
    setTimeout: (fn: () => void, ms: number) => globalThis.setTimeout(fn, ms),
    clearTimeout: (h: unknown) => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>),
  };

  const handlers = new Map<string, Set<WsEventHandler>>();
  let socket: WebSocket | null = null;
  let reconnectAttempt = 0;
  let reconnectHandle: unknown = null;
  let idleCloseHandle: unknown = null;
  let userClosed = false;

  function totalSubscribers(): number {
    let n = 0;
    for (const set of handlers.values()) n += set.size;
    return n;
  }

  function dispatch(envelope: WsEnvelope): void {
    const name = envelope.event ?? envelope.type;
    const targeted = handlers.get(name);
    if (targeted) for (const h of targeted) safeCall(h, envelope);
    const wildcard = handlers.get('*');
    if (wildcard) for (const h of wildcard) safeCall(h, envelope);
  }

  function safeCall(fn: WsEventHandler, envelope: WsEnvelope): void {
    try {
      fn(envelope);
    } catch {
      // Un handler que throwea no debe romper el resto.
    }
  }

  function connect(): void {
    if (userClosed) return;
    if (socket && (socket.readyState === WS.OPEN || socket.readyState === WS.CONNECTING)) return;
    const current = new WS(url);
    socket = current;
    current.addEventListener('open', () => {
      reconnectAttempt = 0;
    });
    current.addEventListener('message', (event: MessageEvent) => {
      let envelope: WsEnvelope;
      try {
        envelope = JSON.parse(String(event.data)) as WsEnvelope;
      } catch {
        return;
      }
      dispatch(envelope);
    });
    current.addEventListener('close', (event: CloseEvent) => {
      // Un socket que se cerró a propósito o que `reconnect()` ya reemplazó no
      // decide nada (antes su `close` pisaba la referencia al nuevo).
      if (socket !== current) return;
      socket = null;
      if (userClosed) return;
      // P-215 (espejo de P-80 en CLI 6.3): un 1008 (auth revocada) o
      // cualquier 4xxx (errores de aplicación definidos por el server)
      // son terminales — reintentar entra en loop con cookie inválida.
      // Marcamos `userClosed` para frenar el backoff y emitimos un
      // pseudo-evento para que la capa React redirija a /login.
      const code = event.code;
      const terminal = code === 1008 || (code >= 4000 && code < 5000);
      if (terminal) {
        userClosed = true;
        dispatch({
          type: '__failed',
          event: '__failed',
          payload: { code, reason: event.reason } as unknown,
        });
        return;
      }
      if (totalSubscribers() === 0) return;
      scheduleReconnect();
    });
    current.addEventListener('error', () => {
      // El close handler se dispara después; aquí solo trackeamos.
    });
  }

  function scheduleReconnect(): void {
    if (userClosed) return;
    if (reconnectAttempt >= reconnectDelays.length) return; // dado por perdido
    const delay = reconnectDelays[Math.min(reconnectAttempt, reconnectDelays.length - 1)];
    reconnectAttempt++;
    reconnectHandle = scheduler.setTimeout(() => {
      reconnectHandle = null;
      connect();
    }, delay);
  }

  function maybeIdleClose(): void {
    if (idleCloseHandle != null) scheduler.clearTimeout(idleCloseHandle);
    idleCloseHandle = scheduler.setTimeout(() => {
      idleCloseHandle = null;
      if (totalSubscribers() === 0) closeSocket();
    }, idleCloseDelay);
  }

  function closeSocket(): void {
    if (reconnectHandle != null) {
      scheduler.clearTimeout(reconnectHandle);
      reconnectHandle = null;
    }
    if (socket) {
      const closing = socket;
      socket = null;
      try {
        closing.close();
      } catch {
        // ignore
      }
    }
  }

  return {
    subscribe(eventName, handler) {
      if (idleCloseHandle != null) {
        scheduler.clearTimeout(idleCloseHandle);
        idleCloseHandle = null;
      }
      let set = handlers.get(eventName);
      if (!set) {
        set = new Set();
        handlers.set(eventName, set);
      }
      set.add(handler);
      // Lazy connect: arrancamos solo al primer suscriptor.
      if (totalSubscribers() === 1) {
        userClosed = false;
        connect();
      }
      return () => {
        const current = handlers.get(eventName);
        if (!current) return;
        current.delete(handler);
        if (current.size === 0) handlers.delete(eventName);
        if (totalSubscribers() === 0) maybeIdleClose();
      };
    },
    readyState() {
      return socket?.readyState ?? WS.CLOSED;
    },
    close() {
      userClosed = true;
      handlers.clear();
      closeSocket();
    },
    reconnect() {
      closeSocket();
      userClosed = false;
      reconnectAttempt = 0;
      if (totalSubscribers() > 0) connect();
    },
  };
}
